/**
 * Error Logger Utility
 * Centralized error logging and tracking system
 * Stores errors in localStorage and can send to external service
 */

import { safeCurrentUrl } from "./safeCurrentUrl";

export interface ErrorLog {
  type: string;
  error: string;
  stack?: string;
  componentStack?: string;
  timestamp: string;
  url: string;
  userAgent: string;
  userId?: string;
  userRole?: string;
  additionalData?: Record<string, any>;
}

const ERROR_LOG_KEY = "error_logs";
const MAX_STORED_ERRORS = 50;

// Optional remote sink: registered at app startup so EVERY logError (incl. global
// window.onerror / unhandledrejection / ErrorBoundary) is also persisted server-side.
// Kept as a setter so this util has no dependency on the supabase service (avoids a
// utils -> services import cycle). Best-effort; must never throw.
type RemoteSink = (errorLog: ErrorLog) => void;
let remoteSink: RemoteSink | null = null;
export const setErrorLogSink = (sink: RemoteSink | null): void => {
  remoteSink = sink;
};

/**
 * Log error to localStorage and console (and the remote sink, if registered)
 */
export const logError = (errorLog: ErrorLog): void => {
  try {
    // Always log to console in development
    if (import.meta.env.DEV) {
      console.error("Error logged:", errorLog);
    }

    // Get existing errors from localStorage
    const existingLogs = getStoredErrors();

    // Add new error to the beginning
    existingLogs.unshift(errorLog);

    // Keep only the most recent errors
    const trimmedLogs = existingLogs.slice(0, MAX_STORED_ERRORS);

    // Store back to localStorage
    localStorage.setItem(ERROR_LOG_KEY, JSON.stringify(trimmedLogs));
  } catch (e) {
    // If localStorage fails, at least log to console
    console.error("Failed to store error log:", e);
    console.error("Original error:", errorLog);
  }

  // Best-effort server persistence (outside the localStorage try so a storage
  // failure doesn't skip it, and a sink failure can't break the caller).
  try {
    remoteSink?.(errorLog);
  } catch {
    /* never propagate */
  }
};

/**
 * Get all stored error logs
 */
export const getStoredErrors = (): ErrorLog[] => {
  try {
    const logs = localStorage.getItem(ERROR_LOG_KEY);
    return logs ? JSON.parse(logs) : [];
  } catch (e) {
    console.error("Failed to retrieve error logs:", e);
    return [];
  }
};

// Alias for backwards compatibility
export const getErrorLogs = getStoredErrors;

/**
 * Clear all stored error logs
 */
export const clearErrorLogs = (): void => {
  try {
    localStorage.removeItem(ERROR_LOG_KEY);
  } catch (e) {
    console.error("Failed to clear error logs:", e);
  }
};

/**
 * Initialize global error handlers
 * Sets up window.onerror and unhandledrejection handlers
 */
export const initializeErrorHandlers = (): void => {
  // Handle uncaught JavaScript errors
  window.onerror = (message, source, lineno, colno, error) => {
    const errorLog: ErrorLog = {
      type: "uncaught_error",
      error: typeof message === "string" ? message : "Unknown error",
      stack: error?.stack,
      timestamp: new Date().toISOString(),
      url: safeCurrentUrl(),
      userAgent: navigator.userAgent,
      additionalData: {
        source,
        lineno,
        colno,
      },
    };

    logError(errorLog);

    // Don't prevent default error handling
    return false;
  };

  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const errorLog: ErrorLog = {
      type: "unhandled_rejection",
      error: event.reason?.message || event.reason || "Unknown rejection",
      stack: event.reason?.stack,
      timestamp: new Date().toISOString(),
      url: safeCurrentUrl(),
      userAgent: navigator.userAgent,
    };

    logError(errorLog);
  });

  console.log("[ErrorLogger] Error handlers initialized");
};

/**
 * Create an error log from a caught error
 */
export const createErrorLog = (
  type: string,
  error: Error | unknown,
  additionalData?: Record<string, any>,
): ErrorLog => {
  const err = error instanceof Error ? error : new Error(String(error));

  return {
    type,
    error: err.message,
    stack: err.stack,
    timestamp: new Date().toISOString(),
    url: safeCurrentUrl(),
    userAgent: navigator.userAgent,
    additionalData,
  };
};
